import React, { Component } from 'react'
import propTypes from 'prop-types'
import { withRouter } from 'react-router'
import { inject } from 'mobx-react'
import { Formik, Form, Field } from 'formik'
import assign from 'lodash/assign'
import pick from 'lodash/pick'
import { clientRecords, parentAccountApi } from '@atheneum/api'

import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import Button from '@components/Button'
import Checkbox from '@components/Form/Checkbox'
import { convertStaticDataToOptions } from '@atheneum/api/dist/lib'
import InfoBox from '@components/InfoBox'
import Input from '@components/Form/Input'
import RadioGroup from '@components/Form/RadioGroup'
import RichTextEditor from '@components/Form/RichTextEditor'
import Tooltip from '@components/Tooltip/Tooltip'
import yup from 'yup'
import WarningIcon from '@components/Icon/Warning'

class ParentAccountForm extends Component {
  static propTypes = {
    parentAccount: propTypes.object,
    globalInvoicingEntity: propTypes.object,
    modalStore: propTypes.object,
    staticData: propTypes.object,
    onSaveSuccess: propTypes.func
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: false
  }

  state = { error: null }

  getInvoicingEntitiesOptions = () => {
    const { parentAccount } = this.props
    if (!parentAccount) return Promise.resolve([])
    return clientRecords.getInvoicingEntities(parentAccount.id).then(res => res.rows)
  }

  saveParentAccount = parentAccountData => {
    const { HEDGE_FUND } = this.props.staticData.CONST.ACCOUNT_TYPES
    if (parentAccountData.accountTypeId !== HEDGE_FUND) {
      parentAccountData.hedgeFundRequired = false
    }

    return this.props.parentAccount
      ? parentAccountApi.update(this.props.parentAccount, parentAccountData)
      : parentAccountApi.create(parentAccountData)
  }

  initializeParentAccount = () => {
    const initialValues = assign(
      pick(this.props.parentAccount, [
        'parentAccountName',
        'parentAccountCode',
        'accountTypeId',
        'hedgeFundRequired',
        'invoicingCoverageId',
        'operationalGuidelines',
        'disclaimerComment',
        'caseCodeRequired',
        'expertsDisclosed'
      ]),
      {
        globalInvoicingEntity: this.props.globalInvoicingEntity
      }
    )
    // Some parent accounts' operationalGuidelines are null in database
    // yet they can't be saved with null
    // Formik+yup was unable to handle this with ensure etc.
    if (!initialValues.operationalGuidelines) {
      return assign(initialValues, { operationalGuidelines: '' })
    }
    return initialValues
  }

  getInitialValues = staticData => {
    return this.props.parentAccount
      ? this.initializeParentAccount()
      : {
          parentAccountName: '',
          parentAccountCode: '',
          accountTypeId: null,
          hedgeFundRequired: true,
          invoicingCoverageId: staticData.CONST.INVOICING_COVERAGES.NONE,
          operationalGuidelines: '',
          disclaimerComment: '',
          caseCodeRequired: true,
          expertsDisclosed: false
        }
  }

  getValidationSchema = (parentAccount, staticData) => {
    return yup.object().shape({
      parentAccountName: yup
        .string()
        .required('Parent account name is required')
        .test(
          'unique',
          'Parent account with this name already exists',
          value => {
            const isValid = yup
              .string()
              .required()
              .isValidSync(value)
            if (isValid) {
              const parentAccountId = parentAccount && parentAccount.id
              return parentAccountApi.checkUniqueName(value, parentAccountId).then(
                res => res.unique
              )
            }
            return true
          }
        ),
      parentAccountCode: yup
        .string()
        .required('Parent account code is required')
        .matches(
          /^[0-9a-zA-Z]{3}$/,
          'Parent account code must be 3 alphanumeric characters'
        )
        .test('unique', 'Parent account code already exists', value => {
          const isValid = yup
            .string()
            .required()
            .matches(/^[0-9a-zA-Z]{3}$/)
            .isValidSync(value)
          if (
            isValid &&
            (!parentAccount || value !== parentAccount.parentAccountCode)
          ) {
            return parentAccountApi.checkUniqueCode(value).then(res => res.unique)
          }
          return true
        }),
      accountTypeId: yup
        .number()
        .integer()
        .typeError('Account type is required'),
      invoicingCoverageId: yup.number().integer(),
      globalInvoicingEntity: yup
        .object()
        .when('invoicingCoverageId', (invoicingCoverageId, schema) => {
          return invoicingCoverageId ===
            staticData.CONST.INVOICING_COVERAGES.GLOBAL && parentAccount
            ? schema
                .required('Invoicing entity is required')
                .typeError('Invoicing entity is required')
            : schema.nullable()
        }),
      operationalGuidelines: yup.string().ensure(),
      disclaimerComment: yup.string().ensure(),
      caseCodeRequired: yup.boolean().nullable(),
      expertsDisclosed: yup.boolean().nullable()
    })
  }

  render () {
    const { staticData, parentAccount, modalStore } = this.props
    const { HEDGE_FUND } = staticData.CONST.ACCOUNT_TYPES

    return (
      <div>
        <Formik
          initialValues={this.getInitialValues(staticData)}
          validationSchema={this.getValidationSchema(parentAccount, staticData)}
          onSubmit={(values, actions) => {
            this.saveParentAccount(values)
              .then(this.props.onSaveSuccess)
              .catch(({ message }) => {
                this.setState({ error: message })
                actions.setSubmitting(false)
              })
          }}
          render={form => (
            <div className='expert-form'>
              <h1 className='expert-form__title'>
                {parentAccount
                  ? `Edit parent account details`
                  : 'Create parent account'}
              </h1>
              {this.state.error ? (
                <InfoBox theme='red' icon={<WarningIcon />}>
                  {this.state.error}
                </InfoBox>
              ) : null}
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>
                    Parent account information
                  </h2>
                  <Field
                    name='parentAccountName'
                    label='Parent account name *'
                    component={Input}
                  />
                  <Field
                    name='parentAccountCode'
                    label='Parent account code *'
                    onChange={e => {
                      form.setFieldValue(
                        'parentAccountCode',
                        e.target.value.toUpperCase(),
                        false
                      )
                      form.setFieldTouched('parentAccountCode', true)
                    }}
                    short
                    component={Input}
                  />
                  <Field
                    name='accountTypeId'
                    label='Account type *'
                    data-cy='account-type-dropdown'
                    placeholder='Select account type'
                    itemToString={item => item && item.name}
                    options={staticData.accountTypes}
                    component={Dropdown}
                  />
                  {form.values.accountTypeId === HEDGE_FUND && (
                    <Field
                      name='hedgeFundRequired'
                      label='Requires Hedge Fund Compliance'
                      component={Checkbox}
                    />
                  )}
                  <Field
                    name='invoicingCoverageId'
                    label='Invoicing coverage'
                    inline
                    options={convertStaticDataToOptions(
                      staticData.invoicingCoverages
                    )}
                    component={RadioGroup}
                  />
                  {form.values.invoicingCoverageId ===
                    staticData.CONST.INVOICING_COVERAGES.GLOBAL && (
                    <Field
                      showResultsOnEmptyInput
                      name='globalInvoicingEntity'
                      label='Global invoicing entity'
                      itemToString={item => item && item.legalName}
                      loadOptions={this.getInvoicingEntitiesOptions}
                      disabled={!parentAccount}
                      component={Autocomplete}
                    />
                  )}
                  <Field
                    name='operationalGuidelines'
                    label='Operational guidelines'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <Field
                    name='disclaimerComment'
                    label={
                      <span>
                        Disclaimer comment
                        <Tooltip tooltip='This text will be added to Expert Invites for Consultation' />
                      </span>
                    }
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <div>
                    <Field
                      name='caseCodeRequired'
                      label='Case code required'
                      component={Checkbox}
                    />
                  </div>
                  <div>
                    <Field
                      name='expertsDisclosed'
                      label='Show submitted expert names'
                      component={Checkbox}
                    />
                  </div>
                  <p className='expert-form__label'>
                    Fields marked with * are required
                  </p>
                </div>
                <div className='expert-form__section expert-form__section--buttons'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={modalStore.close}
                    disabled={form.isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

export default withRouter(inject('modalStore', 'staticData')(ParentAccountForm))
